'use client';

import Link from '@eliadmin/core/src/components/link';
import React from 'react';

import type { GeneralLayout } from '@eliadmin/core/src/api/general/types/general-layout-types';
import type { TDynamicData } from '../../types/dynamic-data';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../shared/heading';
import { LuMinus, LuPlus, LuSend } from 'react-icons/lu';
import { HtmlDescription } from '@eliadmin/core/src/components/ui/html-description';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { MdOutlinePhoneInTalk } from 'react-icons/md';
import { Button } from '../ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';

export function FAQ({
  questions,
  generalLayout,
}: {
  questions: TDynamicData[];
  generalLayout: GeneralLayout;
}) {
  const { t } = useTranslations();

  return (
    <section
      className={cn(
        'custom-section',
        'bg-[#F5F5F5] pb-16 pt-32 sm:pb-32 sm:pt-40 lg:pb-[150px] lg:pt-[240px]',
      )}
    >
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-12 sm:gap-16 lg:gap-20',
        )}
      >
        <Heading title={t('text1088')} />
        <div
          className={cn(
            'custom-container',
            'flex w-full flex-col items-start justify-start gap-12 md:flex-row md:justify-between',
          )}
        >
          <Questions questions={questions} />
          <ContactCard generalLayout={generalLayout} />
        </div>
      </div>
    </section>
  );
}

function Questions({ questions }: { questions: TDynamicData[] }) {
  const disclosureGroup = React.useId();

  return (
    <div
      className={cn(
        'flex w-full flex-col items-start justify-start gap-6 sm:gap-8 md:max-w-[544px]',
        'ea-questions__accordion',
      )}
    >
      {questions.map(question => (
        <details
          key={question.name}
          name={disclosureGroup}
          className={cn('group w-full border-none', 'ea-questions__item')}
        >
          <summary
            className={cn(
              'relative flex w-full cursor-pointer list-none items-center justify-between py-4 text-left text-xl font-normal text-black underline sm:text-2xl lg:text-3xl [&::-webkit-details-marker]:hidden',
              'ea-questions__trigger',
            )}
          >
            <div className="absolute left-0 top-0 flex w-full items-center justify-start">
              <div
                className={cn(
                  'h-[1px] w-[33%] bg-primary transition-all duration-300 ease-in-out group-open:w-full',
                )}
              />
              <div
                className={cn(
                  'h-[1px] w-[67%] bg-[#EBE7E7] transition-all duration-300 ease-in-out group-open:w-0',
                )}
              />
            </div>
            {question.name}
            <LuMinus
              aria-hidden
              className="hidden h-6 w-6 shrink-0 text-primary group-open:block"
            />
            <LuPlus aria-hidden className="h-6 w-6 shrink-0 text-primary group-open:hidden" />
          </summary>
          <div className="pt-4 sm:pt-6 lg:pt-8">
            <HtmlDescription
              color="foreground"
              content={
                question.fields[0]?.value ??
                'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse varius enim in eros elementum tristique. Duis cursus, mi quis viverra ornare, eros dolor interdum nulla, ut commodo diam libero vitae erat. Aenean faucibus nibh et justo cursus id rutrum lorem imperdiet. Nunc ut sem vitae risus tristique posuere.'
              }
              className="ea-faq__description w-full font-normal text-black/70"
              size="sm"
            />
          </div>
        </details>
      ))}
    </div>
  );
}

function ContactCard({ generalLayout }: { generalLayout: GeneralLayout }) {
  const { t } = useTranslations();

  return (
    <div
      className={cn(
        'flex w-full flex-col items-start justify-start gap-6 bg-white p-6 sm:gap-8 sm:p-8 md:max-w-[474px] lg:gap-10 lg:p-10 xl:gap-12 xl:p-12',
        'ea-faq__contact-card',
      )}
    >
      <h3
        data-ea-visual-tag="h4"
        className="text-2xl font-medium tracking-tighter text-black sm:text-3xl"
      >
        {t('text2003')}
      </h3>
      <p className="text-base tracking-tighter sm:text-lg md:text-xl lg:text-2xl">
        {t('text2004')}
      </p>
      <div className={cn('flex w-full flex-col items-start justify-start gap-4')}>
        <Link
          prefetch={false}
          href={`tel:${generalLayout.data.contact?.phone}`}
          className="flex items-center justify-start gap-4 text-lg font-medium sm:text-xl lg:text-2xl xl:gap-6"
        >
          <div className="flex min-h-10 min-w-10 items-center justify-center rounded-full bg-primary sm:min-h-12 sm:min-w-12">
            <LuSend size={24} />
          </div>
          <p className="text-lg font-medium tracking-tighter sm:text-xl lg:text-2xl">
            {generalLayout.data.contact?.phone}
          </p>
        </Link>
        <Link
          prefetch={false}
          href={`mailto:${generalLayout.data.contact?.email}`}
          className="flex items-center justify-start gap-4 xl:gap-6"
        >
          <div className="flex min-h-10 min-w-10 items-center justify-center rounded-full bg-primary sm:min-h-12 sm:min-w-12">
            <MdOutlinePhoneInTalk size={24} />
          </div>
          <p className="text-lg font-medium tracking-tighter sm:text-xl lg:text-2xl">
            {generalLayout.data.contact?.email}
          </p>
        </Link>
      </div>
      <Button asChild variant={'dark'} className="w-full">
        <Link prefetch={false} href={ROUTES.CONTACT()}>
          {t('text82')}
        </Link>
      </Button>
    </div>
  );
}
